/**
 * useAttributeMapSync Hook
 *
 * Provides real-time synchronization between attribute table edits and the map display.
 * When a feature's attribute value is edited in the attribute table, this hook ensures
 * the corresponding OpenLayers feature on the map is immediately updated:
 *
 * 1. Updates the feature's properties on the OpenLayers feature object
 * 2. If the layer uses categorized or graduated symbology based on the changed attribute,
 *    recalculates and applies the per-feature style
 *
 * This avoids the expensive full source.clear() + source.addFeatures() rebuild that
 * the generic layer update mechanism uses, providing instant visual feedback.
 *
 * **Validates: Requirement 13.5** - WHEN a Registered_User edits attributes,
 * THE Attribute_Table_Viewer SHALL immediately reflect changes in the feature properties.
 */

import { useEffect, useRef } from 'react';
import type VectorLayer from 'ol/layer/Vector';
import type VectorSource from 'ol/source/Vector';
import { useAppSelector } from '../store/hooks';
import { symbologyDependsOnField, resolveFeatureStyle } from '../utils/featureStyleResolver';
import type { Layer } from '../store/slices/layersSlice';

/**
 * Tracks the last known feature properties for each layer to detect attribute changes.
 * Key format: "layerId::featureId" → properties snapshot
 */
type PropertiesSnapshot = Map<string, Record<string, any>>;

interface UseAttributeMapSyncOptions {
  /** Reference to the map's vector layers keyed by layer ID */
  vectorLayersRef: React.MutableRefObject<globalThis.Map<string, VectorLayer<VectorSource>>>;
}

/**
 * Hook that synchronizes attribute changes from Redux to OpenLayers features in real-time.
 *
 * Instead of doing a full layer rebuild when a single attribute changes, this hook:
 * 1. Detects which specific features had their properties changed
 * 2. Updates only those features' properties on the OpenLayers feature objects
 * 3. Recalculates per-feature styles if the layer uses categorized/graduated symbology
 *
 * @param options - Configuration including the vector layers ref from the Map component
 */
export function useAttributeMapSync({ vectorLayersRef }: UseAttributeMapSyncOptions): void {
  const layers = useAppSelector((state) => state.layers.layers);
  const previousPropertiesRef = useRef<PropertiesSnapshot>(new Map());

  useEffect(() => {
    const currentSnapshot = previousPropertiesRef.current;
    const newSnapshot: PropertiesSnapshot = new Map();

    for (const layer of layers) {
      if (!layer.features) continue;

      for (const feature of layer.features) {
        const key = `${layer.id}::${feature.id}`;
        const currentProps = feature.properties || {};
        newSnapshot.set(key, currentProps);

        // Check if properties changed from previous snapshot
        const previousProps = currentSnapshot.get(key);
        if (previousProps === undefined) {
          // New feature — skip (handled by the full layer update mechanism)
          continue;
        }

        // Detect which fields changed
        const changedFields = getChangedFields(previousProps, currentProps);
        if (changedFields.length === 0) continue;

        // Apply targeted update to the OpenLayers feature
        applyFeaturePropertyUpdate(
          vectorLayersRef.current,
          layer,
          feature.id,
          currentProps,
          changedFields
        );
      }
    }

    // Update the snapshot reference
    previousPropertiesRef.current = newSnapshot;
  }, [layers, vectorLayersRef]);
}

/**
 * Detects which fields changed between two property objects.
 */
function getChangedFields(previous: Record<string, any>, current: Record<string, any>): string[] {
  const changedFields: string[] = [];
  const allKeys = new Set([...Object.keys(previous), ...Object.keys(current)]);

  for (const key of allKeys) {
    if (previous[key] !== current[key]) {
      changedFields.push(key);
    }
  }

  return changedFields;
}

/**
 * Applies a targeted property update to a specific OpenLayers feature.
 * Updates the feature's properties and recalculates its style if needed.
 */
function applyFeaturePropertyUpdate(
  vectorLayers: globalThis.Map<string, VectorLayer<VectorSource>>,
  layer: Layer,
  featureId: string,
  newProperties: Record<string, any>,
  changedFields: string[]
): void {
  const vectorLayer = vectorLayers.get(layer.id);
  if (!vectorLayer) return;

  const source = vectorLayer.getSource();
  if (!source) return;

  // Find the OpenLayers feature by ID
  const olFeature = source
    .getFeatures()
    .find((f) => (f.getId()?.toString() ?? f.get('id')?.toString() ?? '') === featureId);
  if (!olFeature) return;

  // Update the feature's properties on the OpenLayers feature object
  for (const field of changedFields) {
    olFeature.set(field, newProperties[field], /* silent */ true);
  }

  // Check if any changed field affects the layer's symbology
  const needsStyleRecalculation = changedFields.some((field) =>
    symbologyDependsOnField(layer.symbology, field)
  );

  if (needsStyleRecalculation && layer.symbology && layer.geometryType) {
    // Recalculate per-feature style based on new attribute values
    const newStyle = resolveFeatureStyle(layer.symbology, newProperties, layer.geometryType);

    if (newStyle) {
      olFeature.setStyle(newStyle);
    } else {
      // No matching rule — reset to layer default style
      olFeature.setStyle(undefined as any);
    }

    // Trigger a re-render of the layer to reflect the style change
    source.changed();
  }
}
